// VergleichView.tsx — #vergleich: the multi-scenario compare page. Reuses the
// scenario/results state lifted into app.tsx (both are localStorage-persisted /
// already-computed there) — this view adds no new SIMULATION computation, it only
// presents what app.tsx's auto-compute effect already produced for every scenario at
// once (it DOES do its own read-only fetch of each backtest column's fund price history,
// to show the same honest "Rendite p.a." figure BacktestReturnStats shows in Cockpit —
// see the historyByIsin effect below).
//
// AUTO-COMPUTE (user feedback: "ich will immer beide sehen"): every scenario already has
// a result by the time this view mounts (or gets one within ~200ms) — there is no manual
// "Ausgewählte berechnen" gate anymore. The idle/loading/error states below are purely
// the honest, transient reflection of app.tsx's own auto-compute state, not something the
// user has to trigger.
//
// Layout (rework — the previous column-of-cards layout either left a gap or overflowed
// the row depending on scenario count and viewport, "das war sogar vorher besser"): a
// KENNZAHLEN TABLE, metrics as rows, one column per scenario, built by the pure
// lib/compareTable.ts (row values + best-value highlighting) so this component only
// renders. Fills the row width at any scenario count; scrolls horizontally inside its own
// container (never the page) when there isn't room, with the label column pinned via
// `position: sticky` so it's still readable while scrolled — see global.css's
// .compare-table-wrap. The compact chip row above it is an OPTIONAL include/exclude
// filter (default: every scenario included) for someone with many scenarios who wants to
// narrow the comparison — it governs both the table's columns and the overlay chart's
// series, so there's exactly one "which scenarios am I comparing" control, not two.
import { useEffect, useState } from 'preact/hooks';
import type { RunResult } from '../lib/scenario.js';
import type { ScenarioListEntry } from '../lib/storage.js';
import { percentToBp } from '../lib/convert.js';
import { deflateResult } from '../lib/realTerms.js';
import type { RealTermsSettings } from '../lib/realTerms.js';
import { defaultCompareSelection, sanitizeCompareSelection, toggleCompareSelection } from '../lib/compareSelection.js';
import { anyBacktestColumn, buildCompareRows } from '../lib/compareTable.js';
import type { CompareColumnInput } from '../lib/compareTable.js';
import { backtestPriceCagr } from '../lib/backtestReturns.js';
import type { PricePoint } from '../lib/fundAnalytics.js';
import { ApiError, getFundHistory } from '../lib/api.js';
import { seriesColor } from '../lib/theme.js';
import { CompareChart } from '../components/CompareChart.js';
import type { CompareChartSeries } from '../components/CompareChart.js';
import { RealTermsToggle } from '../components/RealTermsToggle.js';
import { EditIcon } from '../components/icons.js';
import { Hint } from '../components/Hint.js';

export interface VergleichViewProps {
  scenarios: ScenarioListEntry[];
  results: Record<string, RunResult>;
  /** Bypasses the auto-compute cache and forces an immediate recompute of `id` — the
   * per-column "Erneut versuchen" fallback, same role as CockpitView's onRetryScenario. */
  onRetryScenario: (id: string) => void;
  onEditScenario: (id: string) => void;
  realTerms: RealTermsSettings;
  onRealTermsChange: (settings: RealTermsSettings) => void;
}

const IDLE_RUN_RESULT: RunResult = { status: 'idle' };

export function VergleichView({
  scenarios,
  results,
  onRetryScenario,
  onEditScenario,
  realTerms,
  onRealTermsChange,
}: VergleichViewProps) {
  const allIds = scenarios.map((e) => e.id);
  const [selectedRaw, setSelectedRaw] = useState<string[]>(() => defaultCompareSelection(allIds));
  const [historyByIsin, setHistoryByIsin] = useState<Record<string, PricePoint[]>>({});

  const included = sanitizeCompareSelection(selectedRaw, allIds);
  const inflationBp = realTerms.enabled ? percentToBp(realTerms.inflationPercent) : 0;

  // Read-only fetch of every backtest scenario's fund price history (not just the
  // currently-included ones — toggling the include/exclude filter should never re-trigger
  // a fetch/flicker for a column that was already loaded once), purely to show the same
  // honest "Rendite p.a." figure BacktestReturnStats shows in Cockpit — one fetch per
  // distinct ISIN (never re-fetched once cached), never computed for a forecast-mode
  // column (its "return" is an input assumption, not observed data).
  const backtestIsins = Array.from(
    new Set(
      Object.values(results)
        .filter((r): r is RunResult => r.status === 'success' && r.mode === 'backtest' && !!r.meta)
        .map((r) => r.meta!.isin),
    ),
  );
  useEffect(() => {
    const missing = backtestIsins.filter((isin) => !(isin in historyByIsin));
    if (missing.length === 0) return;
    let cancelled = false;
    for (const isin of missing) {
      getFundHistory(isin)
        .then((res) => {
          if (cancelled) return;
          setHistoryByIsin((prev) => ({ ...prev, [isin]: res.prices }));
        })
        .catch(() => {
          if (cancelled) return;
          setHistoryByIsin((prev) => ({ ...prev, [isin]: [] }));
        });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backtestIsins.join(',')]);

  const includedEntries = included
    .map((id) => scenarios.find((e) => e.id === id))
    .filter((e): e is ScenarioListEntry => e != null);

  const columns: CompareColumnInput[] = includedEntries.map((entry) => {
    const run = results[entry.id] ?? IDLE_RUN_RESULT;
    const priceCagr =
      run.status === 'success' && run.mode === 'backtest' && run.meta && historyByIsin[run.meta.isin]
        ? backtestPriceCagr(historyByIsin[run.meta.isin]!, run.meta.usedFrom, run.meta.usedTo)
        : null;
    return { id: entry.id, run, priceCagr };
  });

  const rows = buildCompareRows(columns, inflationBp);
  const showTerFootnote = anyBacktestColumn(columns);

  const overlaySeries: CompareChartSeries[] = includedEntries
    .map((entry) => {
      const run = results[entry.id];
      if (!run || run.status !== 'success' || !run.result) return null;
      const display = deflateResult(run.result, inflationBp);
      return { id: entry.id, label: entry.scenario.label, data: display.series };
    })
    .filter((s): s is CompareChartSeries => s !== null);

  return (
    <div>
      <div class="card">
        <h2>Vergleich</h2>
        <Hint>
          <p class="field-hint">
            Alle Szenarien werden automatisch berechnet und unten verglichen. Optional
            lässt sich die Auswahl einschränken (mind. 1) — z. B. um bei vielen Szenarien
            nur eine Teilmenge nebeneinander zu sehen.
          </p>
        </Hint>
        <div class="chip-row" role="group" aria-label="Szenarien für den Vergleich auswählen">
          {scenarios.map((entry) => (
            <button
              key={entry.id}
              type="button"
              class="chip"
              aria-pressed={included.includes(entry.id)}
              onClick={() => setSelectedRaw(toggleCompareSelection(included, entry.id))}
            >
              {entry.scenario.label}
            </button>
          ))}
        </div>
      </div>

      <div class="card">
        <RealTermsToggle settings={realTerms} onChange={onRealTermsChange} />
      </div>

      <div class="card">
        <h2>
          Kennzahlen
          {realTerms.enabled ? <span class="badge-real-terms">in heutiger Kaufkraft</span> : null}
        </h2>
        <div class="compare-table-wrap">
          <table class="compare-table">
            <thead>
              <tr>
                <th>Kennzahl</th>
                {includedEntries.map((entry, i) => (
                  <th key={entry.id}>
                    <ColumnHead
                      label={entry.scenario.label}
                      color={seriesColor(scenarios.findIndex((e) => e.id === entry.id))}
                      run={results[entry.id] ?? IDLE_RUN_RESULT}
                      onEdit={() => onEditScenario(entry.id)}
                      onRetry={() => onRetryScenario(entry.id)}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td>{row.label}</td>
                  {row.cells.map((cell, i) => (
                    <td key={includedEntries[i]!.id} class={cell.isBest ? 'is-best' : ''} data-label={includedEntries[i]!.scenario.label}>
                      {cell.text}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {showTerFootnote ? <p class="field-hint">* Backtest-TER ist geschätzt und bereits im Kurs enthalten (D-007).</p> : null}
      </div>

      <div class="card">
        <h2>
          Endvermögen im Vergleich
          {realTerms.enabled ? <span class="badge-real-terms">in heutiger Kaufkraft</span> : null}
        </h2>
        {overlaySeries.length > 0 ? (
          <>
            <Hint>
              <p class="field-hint">Klick auf einen Namen unter dem Chart blendet diese Linie ein/aus.</p>
            </Hint>
            <CompareChart series={overlaySeries} />
          </>
        ) : (
          <div class="empty-state">
            <span class="spinner" /> Wird berechnet — das Endvermögen im Zeitverlauf erscheint hier,
            sobald mindestens ein ausgewähltes Szenario berechnet ist.
          </div>
        )}
      </div>
    </div>
  );
}

function ColumnHead({
  label,
  color,
  run,
  onEdit,
  onRetry,
}: {
  label: string;
  color: string;
  run: RunResult;
  onEdit: () => void;
  onRetry: () => void;
}) {
  return (
    <div class="compare-col-head">
      <div class="compare-col-head-top">
        <span class="swatch" style={{ background: color }} />
        <span class="compare-col-name">{label}</span>
        <button type="button" class="compare-col-edit" onClick={onEdit} title="Im Cockpit bearbeiten" aria-label={`${label} im Cockpit bearbeiten`}>
          <EditIcon />
        </button>
      </div>
      {run.status === 'loading' || run.status === 'idle' ? (
        <span class="compare-col-status">
          <span class="spinner" /> wird berechnet…
        </span>
      ) : run.pending ? (
        <span class="compare-col-status">
          <span class="spinner" /> aktualisiert…
        </span>
      ) : run.status === 'error' ? (
        <span class="compare-col-status compare-col-error">
          Fehler: {run.error}{' '}
          <button type="button" class="compare-col-retry" onClick={onRetry}>
            Erneut versuchen
          </button>
        </span>
      ) : null}
    </div>
  );
}
